import React, { useEffect, useState, useCallback } from "react";
import { Col, Container, Row } from "reactstrap";

import ChatBotList from "./ChatBotList";
import UserBotChat from "./UserBotChat";

//Import Breadcrumb
import Breadcrumbs from "../../../../../Components/Common/Breadcrumb";

// Services
import { chatBotApi, ChatSession, Message } from "services/chatbotApi";
import { chatWebSocket } from "services/chatWebSocket";

// Domain context
import { useDomainContext } from "pages/custom/DomainContext";

// Styles
import "./ChatBot.css";

const isTempMessage = (message: Message) => {
  return typeof message.id === 'string' && message.id.startsWith('temp_');
};

const dedupeMessages = (messageList: Message[]): Message[] => {
  const seen = new Set<string>();
  const deduped: Message[] = [];

  for (const msg of messageList) {
    const key = msg.id || `${msg.is_from_user ? 'user' : 'ai'}_${msg.timestamp}_${msg.content}`;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(msg);
    }
  }

  return deduped;
};

const ChatBot = ({ domain }: { domain: number }) => {
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [Chat_Title, setChat_Title] = useState<string>("");
  const [Chat_Box_Bot_Status, setChat_Box_Bot_Status] = useState<'online' | 'connecting' | 'disconnected' | 'offline'>('offline');
  const [currentDomain, setCurrentDomain] = useState<Record<string, any>>({});
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const domainCtx: any = useDomainContext?.() ?? null;

  // Try to populate currentDomain from DomainContext
  useEffect(() => {
    if (domainCtx?.domains && Array.isArray(domainCtx.domains)) {
      const byId = domainCtx.domains.find((d: any) => d?.id === domain)
        || domainCtx.domains.find((d: any) => d?.id === domainCtx?.myParams?.did);
      if (byId && byId !== currentDomain) {
        setCurrentDomain(byId);
      }
    }
  }, [domainCtx, domain]);

  // Fetch chat sessions
  const fetchChats = useCallback(async () => {
    setLoading(true);
    try {
      const sessions = await chatBotApi.getChatSessions(domain);
      setChats(sessions);
      setError(null);
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to load chat sessions");
    } finally {
      setLoading(false);
    }
  }, [domain]);

  // Load initial data
  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  // Set up WebSocket listeners
  useEffect(() => {
    const handleMessage = (message: Message) => {
      console.log('📨 [Frontend] Received message:', {
        id: message.id,
        is_from_user: message.is_from_user,
        content_preview: message.content?.substring(0, 50),
        message_type: message.message_type
      });
      
      setMessages((prev) => {
        let nextMessages = prev;

        if (message.is_from_user) {
          nextMessages = prev.filter(
            (msg) =>
              !(
                isTempMessage(msg) &&
                msg.is_from_user &&
                msg.content === message.content
              )
          );
        } else {
          // Remove any existing AI message with the same id to prevent duplicates
          nextMessages = prev.filter((msg) => msg.id !== message.id);
          // Clear typing indicator when AI message arrives (safety measure in case backend event is delayed)
          setIsTyping(false);
          console.log('✅ [Frontend] AI message received, clearing typing indicator');
        }

        const updatedMessages = dedupeMessages([...nextMessages, message]);
        console.log('📊 [Frontend] Updated messages count:', updatedMessages.length);
        return updatedMessages;
      });
    };

    const handleAiTyping = (data: { isTyping: boolean }) => {
      setIsTyping(data.isTyping);
    };

    const handleSessionInfo = (data: { session: any; messages: Message[] }) => {
      setMessages(dedupeMessages(data.messages));
    };

    const handleError = (data: { error: any }) => {
      console.error("WebSocket error:", data.error);
      // Don't show error for WebSocket unavailable - this is expected if server doesn't support WebSocket
      if (data.error && data.error.toString().includes('WebSocket server unavailable')) {
        console.warn("WebSocket server not available, continuing with REST API only");
      } else {
        setError("Connection error");
      }
    };

    chatWebSocket.on('message', handleMessage);
    chatWebSocket.on('ai_typing', handleAiTyping);
    chatWebSocket.on('session_info', handleSessionInfo);
    chatWebSocket.on('error', handleError);

    return () => {
      chatWebSocket.off('message', handleMessage);
      chatWebSocket.off('ai_typing', handleAiTyping);
      chatWebSocket.off('session_info', handleSessionInfo);
      chatWebSocket.off('error', handleError);
    };
  }, []);

  useEffect(() => {
    const handleConnect = () => setChat_Box_Bot_Status('online');
    const handleDisconnect = () => setChat_Box_Bot_Status(currentSessionId ? 'disconnected' : 'offline');

    chatWebSocket.on('connect', handleConnect);
    chatWebSocket.on('disconnect', handleDisconnect);

    return () => {
      chatWebSocket.off('connect', handleConnect);
      chatWebSocket.off('disconnect', handleDisconnect);
    };
  }, [currentSessionId]);

  useEffect(() => {
    if (!currentSessionId) {
      setChat_Box_Bot_Status('offline');
    }
  }, [currentSessionId]);

  // Clean up WebSocket on unmount
  useEffect(() => {
    return () => {
      chatWebSocket.disconnect();
    };
  }, []);

  // Open chat session
  const userChatOpen = async (chat: ChatSession) => {
    setChat_Title(chat.title || "New Chat");
    setLoading(true);

    // Disconnect from previous session if any before switching
    if (chatWebSocket.isConnected()) {
      chatWebSocket.disconnect();
    }

    setCurrentSessionId(chat.id);
    setChat_Box_Bot_Status("connecting");

    try {

      // Load session details
      const sessionData = await chatBotApi.getChatSession(chat.id);
      setMessages(dedupeMessages(sessionData.messages || []));

      // Try to connect to WebSocket for real-time updates
      try {
        await chatWebSocket.connect(chat.id);
        console.log('WebSocket connected successfully');
        setChat_Box_Bot_Status('online');
      } catch (wsError) {
        console.warn('WebSocket connection failed, using REST API only:', wsError);
        setError("WebSocket connection failed. Real-time features disabled.");
        setChat_Box_Bot_Status('disconnected');
        // Continue without WebSocket - the app will work with REST API only
      }

      setError(null);
    } catch (error) {
      console.error("Error opening chat:", error);
      setError("Failed to open chat session");
    } finally {
      setLoading(false);
    }
  };

  // Create new chat session
  const createNewChat = async () => {
    setLoading(true);
    try {
      const newSession = await chatBotApi.createChatSession(domain);
      setChats((prev) => [newSession, ...prev]);
      await userChatOpen(newSession);
      setError(null);
    } catch (error) {
      console.error("Error creating new chat:", error);
      setError("Failed to create new chat");
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const sendMessage = async (content: string, messageType: string = 'text') => {
    if (!currentSessionId || !content.trim()) return;

    try {
      // Create a temporary user message to show immediately
      const tempUserMessage: Message = {
        id: `temp_${Date.now()}`, // Temporary ID
        chat_session: currentSessionId,
        content: content.trim(),
        message_type: messageType as 'text' | 'image' | 'file' | 'audio' | 'video',
        is_from_user: true,
        timestamp: new Date().toISOString(),
        is_edited: false,
        sender_name: 'You',
        status: {
          status: 'sent',
          timestamp: new Date().toISOString()
        }
      };

      // Add user message to local state immediately
      setMessages((prev) => dedupeMessages([...prev, tempUserMessage]));

      if (chatWebSocket.isConnected()) {
        // Send via WebSocket for real-time experience
        chatWebSocket.sendMessage(content.trim(), messageType);
        // Only refresh chat list if message is sent
        // fetchChats();
      } else {
        // Prevent sending if not connected
        setError("WebSocket not connected. Please wait...");
        // Remove the temporary message if not connected
        setMessages((prev) => prev.filter(msg => msg.id !== tempUserMessage.id));
        return;
      }
      setError(null);
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message");
    }
  };

  // Handle typing indicator
  const handleTyping = (isTyping: boolean) => {
    if (currentSessionId && chatWebSocket.isConnected()) {
      if (isTyping) {
        chatWebSocket.startTyping();
      } else {
        chatWebSocket.stopTyping();
      }
    }
  };

  // Delete chat session
  const deleteChat = async (sessionId: string) => {
    try {
      await chatBotApi.deleteChatSession(sessionId);
      setChats((prev) => prev.filter(chat => chat.id !== sessionId));

      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
        setChat_Title("");
        chatWebSocket.disconnect();
        setChat_Box_Bot_Status('offline');
      }

      setError(null);
    } catch (error) {
      console.error("Error deleting chat:", error);
      setError("Failed to delete chat");
    }
  };

  document.title = "ChatBot | Bizserve";

  const assistantName = currentDomain?.name ? `${currentDomain.name} AI` : undefined;

  return (
    <React.Fragment>
      <div className="page-content pt-0">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumbs title="AI" breadcrumbItem="ChatBot [Beta]" />

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          )}

          <Row>
            <Col lg={12}>
              <div className="d-lg-flex">
                <ChatBotList
                  userChatOpen={userChatOpen}
                  createNewChat={createNewChat}
                  deleteChat={deleteChat}
                  currentSessionId={currentSessionId}
                  currentDomain={currentDomain}
                  chats={chats}
                  loading={loading}
                />

                <div style={{borderLeft: "1px solid #dee2e6", paddingLeft: "20px", flex: 1}}>
                  <UserBotChat
                    Chat_Title={Chat_Title}
                    Chat_Box_Bot_Status={Chat_Box_Bot_Status}
                    messages={messages}
                    setMessages={setMessages}
                    loading={loading}
                    sendMessage={sendMessage}
                    handleTyping={handleTyping}
                    isTyping={isTyping}
                    currentSessionId={currentSessionId}
                    assistantName={assistantName}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

    </React.Fragment>
  );
};



export default ChatBot;
